import { IContactRequestSchema } from '@monetize/types/app';
import React, { useState } from 'react';
import {
  Control,
  Controller,
  FormState,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useACL } from '../../services/acl/acl';
import { MButton, MFormField, MGrid, MGridItem, MInput } from '../Monetize';
import { AddressForm } from './AddressForm';

interface ContactFormProps {
  formId: string;
  isDisabled?: boolean;
  isCompact?: boolean;
  isCompactForCcContact?: boolean;
  omitPrimary?: boolean;
  omitCustomField?: boolean;
  /** from useForm() */
  control: Control<IContactRequestSchema>;
  errors: FormState<IContactRequestSchema>['errors'];
  trigger: UseFormTrigger<IContactRequestSchema>;
  watch: UseFormWatch<IContactRequestSchema>;
  setValue: UseFormSetValue<IContactRequestSchema>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  children?: React.ReactNode;
  isReadOnly?: boolean;
  contactId?: string | null;
}

/**
 * This component is just the form and requires a parent component to manage the form state and submission
 *
 * You can use the formId to set this on a button outside the form for submission - assuming formId is `contact-form`
 * @example `e.x. <button form="contact-form">Submit</button>`
 */
export const ContactForm = React.forwardRef(
  (
    {
      formId,
      isDisabled = false,
      isCompact = false,
      isCompactForCcContact = false,
      omitPrimary,
      omitCustomField,
      control,
      errors,
      trigger,
      watch,
      setValue,
      handleSubmit,
      children,
      isReadOnly: isReadOnlyProp,
      contactId,
    }: ContactFormProps,
    ref,
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const { canDo } = useACL();
    // Disable Edit contact only when contact is being edited
    const isReadOnlyWithFF =
      !canDo([['account_contacts', 'update']]) && !!contactId;
    const isReadOnly = isReadOnlyProp || isReadOnlyWithFF;
    const isReadOnlyProps = {
      isDisabled: isReadOnly || isDisabled,
      isReadOnly,
      variant: isReadOnly ? 'readonly' : 'outline',
    } as const;

    return (
      <form data-testid={formId} id={formId} onSubmit={handleSubmit} noValidate>
        <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
          {isCompactForCcContact ? (
            <>
              <MGridItem colSpan={12}>
                <MFormField error={errors.fullName} label="Name" isRequired>
                  <Controller
                    name="fullName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput {...field} {...isReadOnlyProps} />
                    )}
                  />
                </MFormField>
              </MGridItem>

              <MGridItem colSpan={12}>
                <MFormField error={errors.email} label="Email" isRequired>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput {...field} {...isReadOnlyProps} />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </>
          ) : (
            <>
              <MGridItem colSpan={8}>
                <MFormField error={errors.fullName} label="Name" isRequired>
                  <Controller
                    name="fullName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput {...field} {...isReadOnlyProps} />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={4}>
                <MFormField error={errors.title} label="Job Title">
                  <Controller
                    name="title"
                    control={control}
                    defaultValue=""
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        {...rest}
                        value={value || ''}
                        {...isReadOnlyProps}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={7}>
                <MFormField error={errors.email} label="Email" isRequired>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput {...field} {...isReadOnlyProps} />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={5}>
                <MFormField error={errors.phone} label="Phone">
                  <Controller
                    name="phone"
                    control={control}
                    defaultValue=""
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        value={value || ''}
                        {...rest}
                        {...isReadOnlyProps}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </>
          )}

          {((!isCompactForCcContact && !isCompact) || isExpanded) && (
            <MGridItem colSpan={12}>
              <AddressForm
                isDisabled={isReadOnly}
                isReadOnly={isReadOnly}
                formKeyPrefix="address."
              />
            </MGridItem>
          )}

          {/* children allows parent to embed something, like a button, into the bottom of the form */}
          {isCompact && (
            <>
              <MGridItem colSpan={6}>
                <MButton
                  data-testid="compact-toggle-btn"
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? 'Show Fewer Fields' : 'Show More Fields'}
                </MButton>
              </MGridItem>
              <MGridItem colSpan={6} justifySelf="right">
                {children}
              </MGridItem>
            </>
          )}
          {!isCompact && (
            <MGridItem
              colSpan={12}
              textAlign={isCompactForCcContact ? 'right' : 'left'}
            >
              {children}
            </MGridItem>
          )}
        </MGrid>
      </form>
    );
  },
);
